import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, message } from 'antd';
import './NewsDetail.css';
import Header from './Header';
import Footer from './Footer';
import FloatingSidebar from './FloatingSidebar';
import { newsAPI, imageAPI } from '../services/api';
import { NewsItem } from '../services/api';

const NewsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsItem | null>(null);
  const [relatedNews, setRelatedNews] = useState<NewsItem[]>([]);
  const [headerImage, setHeaderImage] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchNewsDetail();
      fetchHeaderImage();
      fetchRelatedNews();
    }
  }, [id]);

  // 获取新闻详情
  const fetchNewsDetail = async () => {
    try {
      setLoading(true);
      const response = await newsAPI.getNewsById(id!);
      if (response.news) {
        setNews(response.news);
      } else {
        message.error('新闻不存在');
        navigate('/');
      }
    } catch (err) {
      console.error('获取新闻详情失败:', err);
      setError('获取新闻详情失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 获取新闻页头图片
  const fetchHeaderImage = async () => {
    try {
      // 使用正确的分类名称获取新闻页头图片
      const response = await imageAPI.getImagesByCategory('news_header');
      // 由于响应拦截器，response已经是response.data，使用类型断言
      const responseData = response as any;
      if (responseData && responseData.images && responseData.images.length > 0) {
        // 构建正确的图片查看URL
        const imageId = responseData.images[0]._id;
        const imageViewUrl = `http://admin.jieyudayaofang.com/api/images/${imageId}/view?t=${Date.now()}`;
        setHeaderImage(imageViewUrl);
        console.log('获取到新闻页头图片:', responseData.images[0]);
      } else {
        console.log('未找到新闻页头图片，响应结构:', responseData);
      }
    } catch (err) {
      console.error('获取页头图片失败:', err);
    }
  };

  // 获取相关新闻推荐
  const fetchRelatedNews = async () => {
    try {
      const response = await newsAPI.getNews({ limit: 5, status: 'published' });
      if (response.news) {
        // 过滤掉当前新闻
        const filteredNews = response.news.filter(item => item._id !== id);
        setRelatedNews(filteredNews.slice(0, 4)); // 最多显示4条
      }
    } catch (err) {
      console.error('获取相关新闻失败:', err);
    }
  };

  // 格式化日期
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // 如果正在加载，显示加载状态
  if (loading) {
    return (
      <div className="App">
        <Header />
        <div className="news-detail-loading">
          <Spin size="large" />
          <p>加载新闻详情中...</p>
        </div>
        <Footer />
        <FloatingSidebar />
      </div>
    );
  }

  // 如果有错误，显示错误信息
  if (error) {
    return (
      <div className="App">
        <Header />
        <div className="news-detail-error">
          <p>{error}</p>
        </div>
        <Footer />
        <FloatingSidebar />
      </div>
    );
  }

  // 如果没有新闻数据，显示404
  if (!news) {
    return (
      <div className="App">
        <Header />
        <div className="news-detail-404">
          <p>新闻不存在</p>
        </div>
        <Footer />
        <FloatingSidebar />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      
      {/* 顶部图片区域 */}
      <div className="news-detail-hero" style={{ backgroundImage: `url(${headerImage})` }}>
        {/* 移除所有文字内容，只显示图片背景 */}
      </div>

      {/* 主要内容区域 */}
      <div className="news-detail-main">
        <div className="container">
          <div className="news-detail-content">
            {/* 左侧新闻内容 */}
            <div className="news-content-left">
              <div className="news-header">
                <h1 className="news-detail-title">{news.title}</h1>
                <div className="news-meta">
                  <span>发布者: 解郁丸</span>
                  <span>发布时间: {formatDate(news.publishDate)}</span>
                </div>
              </div>
              
              {/* 新闻内容 */}
              <div className="news-body">
                <div 
                  className="news-content"
                  dangerouslySetInnerHTML={{ __html: news.content }}
                />
              </div>
            </div>

            {/* 右侧相关推荐 */}
            <div className="news-sidebar">
              <h3 className="sidebar-title">相关推荐</h3>
              <div className="related-news">
                {relatedNews.map((item) => (
                  <div key={item._id} className="related-news-item" onClick={() => navigate(`/news/${item._id}`)}>
                    <div className="related-news-image">
                      {item.coverImage ? (
                        <img 
                          src={`http://admin.jieyudayaofang.com/api/images/${item.coverImage}/view?t=${Date.now()}`}
                          alt={item.title}
                          onError={(e) => {
                            console.log('相关推荐图片加载失败:', e);
                            console.log('图片ID:', item.coverImage);
                          }}
                        />
                      ) : (
                        <div className="related-news-placeholder">
                          <span>暂无图片</span>
                        </div>
                      )}
                    </div>
                    <div className="related-news-content">
                      <h4 className="related-news-title">{item.title}</h4>
                      <p className="related-news-date">{formatDate(item.publishDate)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <FloatingSidebar />
    </div>
  );
};

export default NewsDetail; 